import React, { ReactElement, memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { getTextColor, getBorders, getTextType, getBackgroundTheme, getStatusColor } from '../../style';
import Container from './Container';
import breakpoints from '../../theme/material/breakpoints';
import { isDarkMode } from 'utils/helpers';

const StyledContainer = styled(Container)<{ customStyleContainer: React.CSSProperties }>(
  {
    width: '100%',
    [breakpoints.up(769)]: {
      padding: '0 70px',
    },
  },
  ({ customStyleContainer }) => customStyleContainer && { ...customStyleContainer }
);

const Nav = styled('div')(() => ({
  backgroundColor: getBackgroundTheme().personalInfo,
  borderBottom: getBorders().DEFAULT,
  borderStyle: 'solid',
  borderColor: 'transparent',
  borderWidth: 0,
  fontSize: 12,
  display: 'flex',
  listStyle: 'none',
  fontWeight: 'bold',
  ...getTextColor().DARK,
  [breakpoints.up(769)]: {
    backgroundColor: 'transparent',
  },
  [breakpoints.down(768.05)]: {
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
}));

const List = styled('ul')<{ customListStyle?: React.CSSProperties }>(
  {
    margin: '0',
    padding: '0',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    width: '100%',
    listStyle: 'none',
    marginBottom: 10,
    [breakpoints.down(768.05)]: {
      marginBottom: 0,
    },
    [breakpoints.down(400.05)]: {
      justifyContent: 'space-around',
    },
  },
  ({ customListStyle }) => customListStyle && { ...customListStyle }
);

const ListItem = styled('li')<{ isSelected: boolean; itemCount: number }>(
  () => ({
    fontWeight: 'bold',
    ...getTextColor().DARK,
    [breakpoints.up(769)]: {
      display: 'block',
    },
    display: 'inline-block',
    textAlign: 'center',
    position: 'relative',
  }),
  ({ isSelected }) =>
    isSelected && {
      color: '#E21D4A',
    },
  ({ itemCount }) =>
    itemCount && {
      width: `${100 / itemCount}%`,
      [breakpoints.down(400.05)]: {
        width: 'auto',
      },
    }
);

const StyledLink = styled(Link)<{ selected: boolean }>(
  () => ({
    display: 'inline-block',
    padding: '22px 8px 8px 8px',
    whiteSpace: 'nowrap',
    ...getTextType().text,
    fontSize: 18,
    lineHeight: '20px',
    letterSpacing: ' -0.3px',
    color: isDarkMode() ? '#AFB0B1' : '#8F8F8F',
    [breakpoints.down(768.05)]: {
      padding: 8,
      fontSize: 13,
      letterSpacing: ' -0.5px',
    },
    [breakpoints.down(320.05)]: {
      padding: 8,
    },
    [breakpoints.down(400.05)]: {
      paddingLeft: 0,
      paddingRight: 0,
    },
  }),
  ({ selected }) =>
    selected && {
      fontWeight: 700,
      color: isDarkMode() ? '#FF3A67' : '#E21D4A',
    }
);

const BorderBottom = styled('div')<{ isSelected: boolean }>(
  {
    borderRadius: 10,
    backgroundColor: 'transparent',
    height: 4,
    width: '100%',
    position: 'relative',
    marginTop: 8,
    [breakpoints.down(639.05)]: {
      marginTop: 4,
    },
  },
  ({ isSelected }) =>
    isSelected && {
      backgroundColor: '#E21D4A',
      '::after': {
        content: '""',
        position: 'absolute',
        top: '-4px',
        left: 0,
        right: 0,
        margin: '0 auto',
        width: 0,
        height: 0,
        borderBottom: 'solid 4px #E21D4A',
        borderLeft: 'solid 4px transparent',
        borderRight: 'solid 4px transparent',
      },
    }
);

const LinkIconContianer = styled('span')`
  display: flex;
  align-items: center;
  gap: 5px;
  ${breakpoints.down(768.05)} {
    gap: 3px;
    & > svg {
      height: 14px;
    }
  }
`;

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const NotificationCount = styled('span')(() => ({
  position: 'absolute',
  right: -14, //offset by padding
  top: -10,
  fontSize: 12,
  color: '#fff',
  backgroundColor: getStatusColor().notification,
  lineHeight: '8px',
  minWidth: 8,
  textAlign: 'center',
  borderRadius: 17,
  padding: 4,
  fontWeight: 600,
}));

const IconPlaceholder = styled('div')({
  position: 'relative',
});

interface TopNavProps {
  subRoutes: any;
  className: string;
  customTopNavStyle?: React.CSSProperties;
  customStyleContainer?: React.CSSProperties;
  customListStyle?: React.CSSProperties;
  children?: React.ReactNode;
  favCount?: number;
  viewCount?: number;
  themeDarkMode?: boolean;
}

const TopNav = ({
  subRoutes,
  className,
  favCount = 0,
  viewCount = 0,
  customTopNavStyle,
  customStyleContainer,
  customListStyle,
  children,
}: TopNavProps): ReactElement => {
  const location = useLocation();
  const getBadge = (routeItem: string) => {
    switch (routeItem) {
      case '/favorites':
      case '/interests/favorited-me':
        return (
          <IconPlaceholder>
            {favCount > 0 && <NotificationCount>{favCount > 99 ? '99+' : favCount}</NotificationCount>}
          </IconPlaceholder>
        );
      case '/interests':
      case '/favorites/viewed-me':
        return (
          <IconPlaceholder>
            {viewCount > 0 && <NotificationCount>{viewCount > 99 ? '99+' : viewCount}</NotificationCount>}
          </IconPlaceholder>
        );
      default:
        return '';
    }
  };

  const buildMenu = () => {
    const activePage = location.pathname;
    const itemCount = Object.keys(subRoutes).length;

    return subRoutes.map(function (routeItem: any) {
      const regex = routeItem.active;
      const active = regex.test(activePage);

      return (
        <ListItem itemCount={itemCount} key={routeItem.url} isSelected={active}>
          <StyledLink to={routeItem.url} selected={active}>
            {getBadge(routeItem.url)}
            <LinkIconContianer>
              {active ? routeItem.icon.active : routeItem.icon.default} {routeItem.page}
            </LinkIconContianer>
            <BorderBottom isSelected={active} />
          </StyledLink>
        </ListItem>
      );
    });
  };

  return (
    <Nav className={className} data-test-id={className} style={customTopNavStyle}>
      <StyledContainer customStyleContainer={customStyleContainer} fullWidth>
        <List id="top-nav-list" customListStyle={customListStyle}>
          {buildMenu()}
        </List>
      </StyledContainer>
      {children}
    </Nav>
  );
};

export default memo(TopNav);
